import {
  ApiBlueprint,
  configApiRef,
  coreExtensionData,
  createExtension,
  createExtensionInput,
  createFrontendModule,
  oauthRequestApiRef,
  PageLayout,
  pluginHeaderActionsApiRef,
  useApi,
  type IconComponent,
  type PageLayoutProps,
  type PendingOAuthRequest,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import {
  createElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useObservable from 'react-use/esm/useObservable';
import { Link, useLocation } from 'react-router-dom';
import { isExpectedDirectoryPickerAbort } from '../components/Root/browserGuards';
import { appRoutePaths } from '../components/Root/navigation';
import { Root } from '../components/Root/Root';

const isTechDocsReaderRoute = (pathname: string) => {
  const docsRouteSegments = pathname
    .slice(appRoutePaths.docs.length)
    .split('/')
    .filter(Boolean);

  return (
    pathname.startsWith(`${appRoutePaths.docs}/`) &&
    docsRouteSegments.length >= 3
  );
};

const routeClassMatchers = [
  {
    className: 'tegel-home-route',
    matches: (pathname: string) => pathname === appRoutePaths.home,
  },
  {
    className: 'tegel-catalog-index-route',
    matches: (pathname: string) => pathname === appRoutePaths.catalog,
  },
  {
    className: 'tegel-catalog-entity-route',
    matches: (pathname: string) =>
      pathname.startsWith(`${appRoutePaths.catalog}/`),
  },
  {
    className: 'tegel-api-docs-route',
    matches: (pathname: string) =>
      pathname === appRoutePaths.apis ||
      pathname.startsWith(`${appRoutePaths.apis}/`),
  },
  {
    className: 'tegel-docs-index-route',
    matches: (pathname: string) => pathname === appRoutePaths.docs,
  },
  {
    className: 'tegel-techdocs-reader-route',
    matches: isTechDocsReaderRoute,
  },
  {
    className: 'tegel-catalog-import-route',
    matches: (pathname: string) =>
      pathname === appRoutePaths.import ||
      pathname.startsWith(`${appRoutePaths.import}/`),
  },
  {
    className: 'tegel-catalog-graph-route',
    matches: (pathname: string) =>
      pathname === appRoutePaths.catalogGraph ||
      pathname.startsWith(`${appRoutePaths.catalogGraph}/`),
  },
  {
    className: 'tegel-groups-route',
    matches: (pathname: string) =>
      pathname === appRoutePaths.groups ||
      pathname.startsWith(`${appRoutePaths.groups}/`),
  },
  {
    className: 'tegel-access-route',
    matches: (pathname: string) =>
      pathname === appRoutePaths.access ||
      pathname.startsWith(`${appRoutePaths.access}/`),
  },
  {
    className: 'tegel-search-route',
    matches: (pathname: string) =>
      pathname === appRoutePaths.search ||
      pathname.startsWith(`${appRoutePaths.search}/`),
  },
  {
    className: 'tegel-settings-route',
    matches: (pathname: string) =>
      pathname === appRoutePaths.settings ||
      pathname.startsWith(`${appRoutePaths.settings}/`),
  },
] as const;

const ExpectedBrowserRejectionGuard = () => {
  useEffect(() => {
    const isExpectedDirectoryPickerCancel = (error: Error | undefined) => {
      const message = error?.message ?? '';

      return (
        isExpectedDirectoryPickerAbort(error) &&
        message.includes('showDirectoryPicker')
      );
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as Error | undefined;
      const message = reason?.message ?? '';

      if (
        reason?.name === 'RejectedError' ||
        message.includes('Login failed, rejected by user') ||
        isExpectedDirectoryPickerCancel(reason)
      ) {
        event.preventDefault();
      }
    };

    const handleWindowError = (event: ErrorEvent) => {
      if (isExpectedDirectoryPickerCancel(event.error as Error | undefined)) {
        event.preventDefault();
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleWindowError);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
      window.removeEventListener('error', handleWindowError);
    };
  }, []);

  return null;
};

const DrivelineShellRouteClasses = () => {
  const location = useLocation();

  useEffect(() => {
    routeClassMatchers.forEach(({ className, matches }) => {
      document.documentElement.classList.toggle(
        className,
        matches(location.pathname),
      );
    });

    return () => {
      routeClassMatchers.forEach(({ className }) => {
        document.documentElement.classList.remove(className);
      });
    };
  }, [location.pathname]);

  return null;
};

const renderOAuthProviderIcon = (
  icon: PendingOAuthRequest['provider']['icon'],
) => {
  if (!icon) {
    return null;
  }

  if (isValidElement(icon)) {
    return icon;
  }

  return createElement(icon as IconComponent, { fontSize: 'large' });
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
};

const DrivelineOAuthRequestDialog = () => {
  const [busyProvider, setBusyProvider] = useState<string>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const oauthRequestApi = useApi(oauthRequestApiRef);
  const configApi = useApi(configApiRef);

  const requests = useObservable(
    useMemo(() => oauthRequestApi.authRequest$(), [oauthRequestApi]),
    [],
  );

  const authRedirect =
    configApi.getOptionalBoolean('enableExperimentalRedirectFlow') ?? false;
  const appTitle = configApi.getOptionalString('app.title') ?? 'Driveline';

  if (!requests.length) {
    return null;
  }

  const rejectAll = () => {
    requests.forEach(request => request.reject());
    setErrors({});
  };

  const continueRequest = async (request: PendingOAuthRequest) => {
    const providerTitle = request.provider.title;

    setBusyProvider(providerTitle);
    setErrors(current => {
      const { [providerTitle]: _ignored, ...rest } = current;
      return rest;
    });

    try {
      await request.trigger();
    } catch (error) {
      setErrors(current => ({
        ...current,
        [providerTitle]: getErrorMessage(error),
      }));
    } finally {
      setBusyProvider(undefined);
    }
  };

  return (
    <div className="tegel-oauth-dialog" role="presentation">
      <div className="tegel-oauth-dialog__backdrop" />
      <main
        aria-labelledby="tegel-oauth-dialog-title"
        aria-modal="true"
        className="tegel-oauth-dialog__surface"
        role="dialog"
      >
        <header className="tegel-oauth-dialog__header">
          <p className="tegel-oauth-dialog__eyebrow">Login required</p>
          <h2 id="tegel-oauth-dialog-title">Connect account access</h2>
          {authRedirect && (
            <p className="tegel-oauth-dialog__hint">
              This will open the OAuth sign-in flow.
            </p>
          )}
        </header>

        <div className="tegel-oauth-dialog__requests">
          {requests.map(request => {
            const providerTitle = request.provider.title;
            const message =
              request.provider.message ??
              `Sign in to allow ${appTitle} access to ${providerTitle} APIs and identities.`;
            const isBusy = busyProvider === providerTitle;
            const icon = renderOAuthProviderIcon(request.provider.icon);

            return (
              <section
                className="tegel-oauth-dialog__request"
                key={providerTitle}
              >
                {icon && (
                  <div
                    aria-hidden="true"
                    className="tegel-oauth-dialog__provider-icon"
                  >
                    {icon}
                  </div>
                )}
                <div className="tegel-oauth-dialog__request-copy">
                  <h3>{providerTitle}</h3>
                  <p>{message}</p>
                  {errors[providerTitle] && (
                    <p className="tegel-oauth-dialog__error">
                      {errors[providerTitle]}
                    </p>
                  )}
                </div>
                <button
                  className="tegel-oauth-dialog__primary"
                  disabled={Boolean(busyProvider)}
                  onClick={() => continueRequest(request)}
                  type="button"
                >
                  {isBusy ? 'Opening...' : 'Log in'}
                </button>
              </section>
            );
          })}
        </div>

        <footer className="tegel-oauth-dialog__footer">
          <button
            className="tegel-oauth-dialog__secondary"
            disabled={Boolean(busyProvider)}
            onClick={rejectAll}
            type="button"
          >
            Reject all
          </button>
        </footer>
      </main>
    </div>
  );
};

const DrivelineAppLayout = ({ children }: { children: JSX.Element }) => (
  <>
    <ExpectedBrowserRejectionGuard />
    <DrivelineOAuthRequestDialog />
    <DrivelineShellRouteClasses />
    <Root>{children}</Root>
  </>
);

const appModuleLayout = createExtension({
  name: 'layout',
  attachTo: { id: 'app/root', input: 'children' },
  inputs: {
    content: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
  },
  output: [coreExtensionData.reactElement],
  factory({ inputs }) {
    return [
      coreExtensionData.reactElement(
        <DrivelineAppLayout>
          {inputs.content.get(coreExtensionData.reactElement)}
        </DrivelineAppLayout>,
      ),
    ];
  },
});

const isSchemeCharacter = (character: string) => {
  const code = character.charCodeAt(0);
  return (
    (code >= 97 && code <= 122) ||
    (code >= 65 && code <= 90) ||
    (code >= 48 && code <= 57) ||
    character === '+' ||
    character === '.' ||
    character === '-'
  );
};

const hasExternalScheme = (href: string) => {
  const separatorIndex = href.indexOf(':');

  if (separatorIndex <= 0) {
    return false;
  }

  const firstCode = href.charCodeAt(0);
  if (
    !(
      (firstCode >= 97 && firstCode <= 122) ||
      (firstCode >= 65 && firstCode <= 90)
    )
  ) {
    return false;
  }

  for (let index = 1; index < separatorIndex; index++) {
    if (!isSchemeCharacter(href[index])) {
      return false;
    }
  }

  return true;
};

const isExternalOrAnchorHref = (href: string) =>
  href.startsWith('#') || hasExternalScheme(href);

const stripTrailingSlash = (value: string) => {
  if (value.length > 1 && value.endsWith('/')) {
    return value.slice(0, -1);
  }

  return value || '/';
};

const splitHrefPath = (href: string) => {
  const queryIndex = href.indexOf('?');
  const hashIndex = href.indexOf('#');
  const candidates = [queryIndex, hashIndex].filter(index => index >= 0);
  const separatorIndex = candidates.length > 0 ? Math.min(...candidates) : -1;

  return separatorIndex >= 0 ? href.slice(0, separatorIndex) : href;
};

const normalizeInternalPath = (value: string) => {
  const segments = value.split('/').filter((segment, index) => {
    return index === 0 || segment.length > 0;
  });

  const normalized = segments.join('/');
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const resolvePageLayoutHref = (href: string, pathname: string) => {
  if (isExternalOrAnchorHref(href) || href.startsWith('/')) {
    return href;
  }

  const normalizedHref = href.startsWith('./') ? href.slice(2) : href;

  if (!normalizedHref) {
    return pathname || '/';
  }

  const basePath = pathname.endsWith('/')
    ? pathname
    : pathname.slice(0, pathname.lastIndexOf('/') + 1);

  return normalizeInternalPath(`${basePath}${normalizedHref}`);
};

const isPageLayoutTabActive = (href: string, pathname: string) => {
  if (isExternalOrAnchorHref(href)) {
    return false;
  }

  const normalizedHrefPath = stripTrailingSlash(splitHrefPath(href));
  const normalizedPathname = stripTrailingSlash(pathname);

  return (
    normalizedPathname === normalizedHrefPath ||
    normalizedPathname.startsWith(`${normalizedHrefPath}/`)
  );
};

const DrivelinePageLayout = ({
  icon,
  title,
  headerActions,
  tabs,
  children,
  noHeader,
}: PageLayoutProps) => {
  const location = useLocation();

  if (noHeader) {
    return <>{children}</>;
  }

  return (
    <div className="driveline-frontend-page-layout">
      {(title || tabs) && (
        <header className="driveline-frontend-page-layout__header">
          {title && (
            <div className="driveline-frontend-page-layout__title-block">
              <div className="driveline-frontend-page-layout__title-row">
                {icon}
                <h1>{title}</h1>
                {headerActions && (
                  <div className="driveline-frontend-page-layout__actions">
                    {headerActions}
                  </div>
                )}
              </div>
            </div>
          )}
          {tabs && tabs.length > 0 && (
            <nav
              className="driveline-frontend-page-layout__tabs"
              aria-label={`${title ?? 'Page'} sections`}
            >
              {tabs.map(tab => {
                const href = resolvePageLayoutHref(tab.href, location.pathname);
                const isActive = isPageLayoutTabActive(href, location.pathname);

                const className = isActive ? 'is-active' : undefined;
                const ariaCurrent = isActive ? 'page' : undefined;
                const tabContent = (
                  <>
                    {tab.icon}
                    <span>{tab.label}</span>
                  </>
                );

                if (isExternalOrAnchorHref(href)) {
                  return (
                    <a
                      key={tab.id}
                      href={href}
                      className={className}
                      aria-current={ariaCurrent}
                    >
                      {tabContent}
                    </a>
                  );
                }

                return (
                  <Link
                    key={tab.id}
                    to={href}
                    className={className}
                    aria-current={ariaCurrent}
                  >
                    {tabContent}
                  </Link>
                );
              })}
            </nav>
          )}
        </header>
      )}
      <div className="driveline-frontend-page-layout__content">{children}</div>
    </div>
  );
};

const appModulePageLayout = SwappableComponentBlueprint.make({
  name: 'core-page-layout',
  params: defineParams =>
    defineParams({
      component: PageLayout,
      loader: () => DrivelinePageLayout,
    }),
});

const appModuleHeaderActions = ApiBlueprint.make({
  name: 'plugin-header-actions',
  params: defineParams =>
    defineParams({
      api: pluginHeaderActionsApiRef,
      deps: {},
      factory: () => ({
        getPluginHeaderActions: () => [],
      }),
    }),
});

export const appModuleShell = createFrontendModule({
  pluginId: 'app',
  extensions: [appModuleLayout, appModulePageLayout, appModuleHeaderActions],
});
